import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { Injectable } from '@angular/core';
import { SettingsService } from '../settings/settings.service';
import { TtsService } from '../speech/tts.service';
import { ReadingBoundary, ReadingUnitType, ReaderStatus, SoundStatus, SpeakingJobType } from '../../core/reading-utils/enums';
import { HighlightingTypes } from '../../core/pdf-utils/enums';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { ReadingService } from '../text/reading.service';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { ISettings } from 'src/app/core/interfaces';
import { DocumentManagerService } from './document-manager.service';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentHighlightService {
  private get settings(): ISettings { return this.settingsService.settings; }
  private get currentDoc(): AsgardDocBase | null | undefined { return this.docMgrService.currentDoc; }

  constructor(
    private readonly settingsService: SettingsService,
    private readonly ttsService: TtsService,
    private readonly readingService: ReadingService,
    private readonly docMgrService: DocumentManagerService,
    private readonly autosave: AutosaveManagerService,
  ) {
    this.initSubscriptions();
  }

  cleanCurrentHighlight(force: boolean = false, clicked: boolean = false): void {
    if (this.currentDoc && this.currentDoc.isDocumentReady) {
      this.currentDoc.clearTextHighlight(force, clicked);
    } else {
      console.warn('Impossible to clean selection if document not ready.');
    }
  }

  async highlightCurrentDocItem(): Promise<void> {
    if (this.currentDoc && !this.currentDoc.hasReadingItem || this.ttsService.activeJobType === SpeakingJobType.SimpleText) {
      return;
    }

    if (this.currentDoc && this.currentDoc.readingItem) {
      const highlightType = this.currentDoc.readingItem.length > 1 ? HighlightingTypes.Sentence : HighlightingTypes.Word;
      this.currentDoc.doHighlight(this.currentDoc.readingItem, this.settings.readingUnitColor, highlightType);
    }
  }

  private async highlightWordByIdx(wordIdx: number, exclusive: boolean = false): Promise<void> {
    if (this.currentDoc) {
      const words = this.currentDoc.hasReadingItem ? this.currentDoc.readingItem : null;
      const word = words !== null ? words.find(x => x.itemIdx === wordIdx) || null : null;

      if (word === null) {
        console.error('Wrong word index received in "highlightWordIdx".');
        return;
      }
      this.currentDoc.doHighlight([word], this.settings.currentWordColor, HighlightingTypes.Word, exclusive);
    }
  }

  onReadingItemUpdated(docId: string): void {
    if (this.currentDoc === null || this.currentDoc === undefined) { return; }
    if (this.currentDoc.readingItem === null) { return; }

    if (!this.currentDoc || docId !== this.currentDoc.documentId || !this.currentDoc.hasReadingItem) {
      if (this.currentDoc) {
        this.autosave.updateReadingPosition(
          this.currentDoc.docHash,
          this.currentDoc.getReadingPostition(),
          this.currentDoc.getCurrentPageIdx()
        );
      }
      return;
    }

    const itemType = this.currentDoc.readingItem.length > 1 ? ReadingUnitType.Sentence : ReadingUnitType.Word;
    if (this.currentDoc instanceof AsgardDocHtml && this.currentDoc.isEditable) {
      if (this.currentDoc instanceof AsgardDocFroala && this.readingService.readingStatus !== ReaderStatus.Speaking) {
        if (!this.currentDoc.isReadingItemClicked) { // don't move cursor to item start by user click
          (this.currentDoc as AsgardDocFroala).moveCursorToCurrentItemStart(itemType);
          this.currentDoc.clearTextHighlight(true);
        }
      }
      this.autosave.updateReadingPosition(
        this.currentDoc.docHash,
        this.currentDoc.getReadingPostition(),
        this.currentDoc.getCurrentPageIdx()
      );

      return;
    }
    this.currentDoc.clearTextHighlight(true);

    if (this.readingService.readingStatus === ReaderStatus.Speaking) { // highlight item when speaking
      this.highlightCurrentDocItem();
    } else { // highlight clicked words for all not Froala documents
      const readingItems = this.currentDoc.readingItem;
      if (readingItems) {
        const isTxtNote = this.currentDoc instanceof AsgardDocPdf &&
          (this.currentDoc as AsgardDocPdf).isItemTextNote(readingItems[0].itemIdx);

        if (!isTxtNote) { // skip draw highlight rects on text notes
          this.highlightWordByIdx(readingItems[0].itemIdx, true);
        }
      }
    }
    this.autosave.updateReadingPosition(
      this.currentDoc.docHash,
      this.currentDoc.getReadingPostition(),
      this.currentDoc.getCurrentPageIdx()
    );
  }

  private initSubscriptions(): void {
    this.readingService.readingStatusObservable.subscribe(x => {
      if (x === ReaderStatus.Speaking && this.ttsService.activeJobType === SpeakingJobType.Tooltip) {
        this.cleanCurrentHighlight(true);
      }

      if (this.currentDoc && x === ReaderStatus.Speaking && !this.currentDoc.isUnitHighlighted(this.settings.readingUnit)) {
        this.highlightCurrentDocItem(); // hightlight sentence on speech started (and readingItem already set)
      }
    });

    this.ttsService.soundStatus.subscribe(x => {
      if (this.ttsService.activeJobType !== SpeakingJobType.Document && this.ttsService.activeJobType !== SpeakingJobType.AltText) {
        return;
      }

      if (x === SoundStatus.Error) {
        if (this.currentDoc) {
          this.currentDoc.clearTextHighlight(true);
        }
      }
    });

    this.ttsService.readingPosition.subscribe(msg => {
      if (msg === null || this.ttsService.activeJobType === SpeakingJobType.SimpleText) {
        return;
      }

      if (msg.boundaryType === ReadingBoundary.Word 
        && (this.ttsService.activeJobType === SpeakingJobType.Document || this.ttsService.activeJobType === SpeakingJobType.Link)
      ) {
        this.highlightWordByIdx(msg.wordIdx);
        return;
      }

      if (msg.boundaryType === ReadingBoundary.Text && this.ttsService.activeJobType === SpeakingJobType.Document) {
        this.cleanCurrentHighlight(true);

        if (!msg.batchFinished && this.readingService.readingStatus === ReaderStatus.Speaking) {
          this.highlightCurrentDocItem(); // highlight sentence on every next sentence started to read
        }
      }
    });
  }
}
